import React, { useState } from 'react';
import { 
  TerminalIcon, 
  CopyIcon, 
  CheckIcon, 
  ShieldCheckIcon,
  DownloadIcon,
  CommandIcon,
  WalletIcon,
  ArrowRightIcon,
  ExternalLinkIcon
} from 'lucide-react';
import { TokenLogos } from './assets/tokenLogos';
import './index.css';

function CLIPage() {
  const [copiedCommand, setCopiedCommand] = useState(null);

  const copyToClipboard = (text, commandId) => {
    navigator.clipboard.writeText(text);
    setCopiedCommand(commandId);
    setTimeout(() => setCopiedCommand(null), 2000);
  };

  const CodeBlock = ({ command, id, label }) => (
    <div className="relative group">
      {label && (
        <div className="text-xs text-white/50 mb-2 font-mono">{label}</div>
      )}
      <div className="bg-black/60 border border-white/10 rounded-xl p-4 font-mono text-sm overflow-x-auto">
        <div className="flex items-center justify-between">
          <code className="text-green-400 whitespace-pre-wrap break-all">{command}</code>
          <button
            onClick={() => copyToClipboard(command, id)}
            className="ml-4 p-2 hover:bg-white/10 rounded-lg transition-colors flex-shrink-0"
            title="Copy to clipboard"
          >
            {copiedCommand === id ? (
              <CheckIcon className="w-4 h-4 text-green-400" />
            ) : (
              <CopyIcon className="w-4 h-4 text-white/60 group-hover:text-white" />
            )}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-950 to-black relative overflow-hidden">
      {/* Background Animation */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-72 h-72 bg-blue-600 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-float"></div>
        <div className="absolute top-0 right-0 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-float animation-delay-2000"></div>
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-blue-700 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-float animation-delay-4000"></div>
      </div>

      {/* Header */}
      <header className="relative z-10 p-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <a href="/" className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
            <div className="w-14 h-14 rounded-xl overflow-hidden">
              <img src={TokenLogos.GIGACHAD} alt="Chad Swap Logo" className="w-full h-full object-cover" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white font-orbitron">Chad Swap</h1>
              <p className="text-white/60 text-sm">Terminal Interface</p>
            </div>
          </a>

          <div className="flex items-center space-x-4">
            <a
              href="/"
              className="px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white rounded-xl transition-colors duration-200"
            >
              Web App
            </a>
            <a
              href="https://github.com/GFTM/swap"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white rounded-xl transition-colors duration-200"
            >
              <span>GitHub</span>
              <ExternalLinkIcon className="w-4 h-4" />
            </a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 p-6">
        <div className="max-w-4xl mx-auto">
          
          {/* Hero Section */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-green-500/20 border border-green-400/30 rounded-full mb-6">
              <TerminalIcon className="w-4 h-4 text-green-400" />
              <span className="text-green-400 text-sm font-medium">Terminal Interface</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 font-orbitron">
              ChadSwap CLI
            </h2>
            <p className="text-xl text-white/70 font-rajdhani max-w-2xl mx-auto">
              Swap tokens directly from your terminal with hardware wallet support.
              Secure, fast, and chad-pilled. 🚀
            </p>
          </div>

          {/* Quick Install Section */}
          <div className="swap-card rounded-2xl p-8 mb-8">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-3 bg-blue-500/20 rounded-xl">
                <DownloadIcon className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white font-orbitron">Quick Install</h3>
                <p className="text-white/60 text-sm">Install from crates.io</p>
              </div>
            </div>

            <CodeBlock 
              command="cargo install chadswap"
              id="install"
              label="Install via Cargo (requires Rust)"
            />

            <div className="mt-4 p-4 bg-blue-500/10 border border-blue-400/20 rounded-xl">
              <p className="text-white/70 text-sm mb-2">
                Don't have Rust? Install it first:
              </p>
              <CodeBlock 
                command="curl --proto '=https' --tlsv1.2 -sSf https://sh.rustup.rs | sh"
                id="rustup"
              />
            </div>

            <div className="mt-4">
              <p className="text-white/60 text-sm text-center">
                View on <a href="https://crates.io/crates/chadswap" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">crates.io</a>
              </p>
            </div>
          </div>

          {/* What Gets Installed */}
          <div className="swap-card rounded-2xl p-8 mb-8">
            <h3 className="text-xl font-bold text-white mb-6 font-orbitron">What You Get</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                <div className="text-2xl mb-2">💱</div>
                <h4 className="text-white font-semibold mb-1">Token Swaps</h4>
                <p className="text-white/60 text-sm">Swap FTM, LIF3, LSHARE and more</p>
              </div>
              <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                <div className="text-2xl mb-2">💰</div>
                <h4 className="text-white font-semibold mb-1">Balance Check</h4>
                <p className="text-white/60 text-sm">View all your token balances</p>
              </div>
              <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                <div className="text-2xl mb-2">🎨</div>
                <h4 className="text-white font-semibold mb-1">Beautiful TUI</h4>
                <p className="text-white/60 text-sm">Colorful interactive terminal interface</p>
              </div>
            </div>
          </div>

          {/* Usage Section */}
          <div className="swap-card rounded-2xl p-8 mb-8">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-3 bg-purple-500/20 rounded-xl">
                <CommandIcon className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white font-orbitron">Usage</h3>
                <p className="text-white/60 text-sm">Set up your wallet and start swapping</p>
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <p className="text-white/70 mb-3">1. Set your private key:</p>
                <CodeBlock command="export PRIVATE_KEY=your_private_key_here" id="setkey" />
                <p className="text-white/50 text-xs mt-2">⚠️ Never share your private key!</p>
              </div>

              <div>
                <p className="text-white/70 mb-3">2. Launch ChadSwap (interactive mode):</p>
                <CodeBlock command="chadswap" id="launch" />
              </div>

              <div className="border-t border-white/10 pt-6">
                <p className="text-white/70 mb-4">Or use direct commands:</p>
                <div className="space-y-3">
                  <CodeBlock command="chadswap balance" id="balance" label="Check your token balances" />
                  <CodeBlock command="chadswap swap" id="swap" label="Start an interactive swap" />
                  <CodeBlock command="chadswap config" id="config" label="View configuration" />
                </div>
              </div>
            </div>
          </div>

          {/* Features Section */}
          <div className="swap-card rounded-2xl p-8 mb-8">
            <h3 className="text-xl font-bold text-white mb-6 font-orbitron">Features</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-start space-x-3 p-4 bg-white/5 rounded-xl">
                <WalletIcon className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-white font-semibold mb-1">Private Key Wallet</h4>
                  <p className="text-white/60 text-sm">Sign transactions directly with your private key</p>
                </div>
              </div>
              <div className="flex items-start space-x-3 p-4 bg-white/5 rounded-xl">
                <TerminalIcon className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-white font-semibold mb-1">Interactive Menus</h4>
                  <p className="text-white/60 text-sm">Beautiful numbered menus for easy navigation</p>
                </div>
              </div>
              <div className="flex items-start space-x-3 p-4 bg-white/5 rounded-xl">
                <ArrowRightIcon className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-white font-semibold mb-1">Real-time Quotes</h4>
                  <p className="text-white/60 text-sm">See exact output amounts and slippage before swapping</p>
                </div>
              </div>
              <div className="flex items-start space-x-3 p-4 bg-white/5 rounded-xl">
                <ShieldCheckIcon className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-white font-semibold mb-1">No Browser Required</h4>
                  <p className="text-white/60 text-sm">Works entirely in your terminal - pure CLI power</p>
                </div>
              </div>
            </div>
          </div>

          {/* Supported Tokens */}
          <div className="swap-card rounded-2xl p-8 mb-8">
            <h3 className="text-xl font-bold text-white mb-6 font-orbitron text-center">Supported Tokens</h3>
            <div className="flex justify-center items-center space-x-8">
              <div className="flex flex-col items-center space-y-2">
                <img src={TokenLogos.FTM} alt="FTM" className="w-12 h-12" />
                <span className="text-white text-sm font-medium">FTM</span>
              </div>
              <div className="flex flex-col items-center space-y-2">
                <img src={TokenLogos.WFTM} alt="WFTM" className="w-12 h-12" />
                <span className="text-white text-sm font-medium">WFTM</span>
              </div>
              <div className="flex flex-col items-center space-y-2">
                <img src={TokenLogos.LIF3} alt="LIF3" className="w-12 h-12" />
                <span className="text-white text-sm font-medium">LIF3</span>
              </div>
              <div className="flex flex-col items-center space-y-2">
                <img src={TokenLogos.LSHARE} alt="LSHARE" className="w-12 h-12" />
                <span className="text-white text-sm font-medium">LSHARE</span>
              </div>
            </div>
          </div>

          {/* Configuration Section */}
          <div className="swap-card rounded-2xl p-8 mb-8">
            <h3 className="text-xl font-bold text-white mb-6 font-orbitron">Configuration</h3>
            <p className="text-white/70 mb-4">
              After installation, a config file is created at:
            </p>
            <CodeBlock command="~/.config/chadswap/config.toml" id="config-path" />
            
            <div className="mt-6">
              <p className="text-white/70 mb-3">Default configuration:</p>
              <div className="bg-black/60 border border-white/10 rounded-xl p-4 font-mono text-sm overflow-x-auto">
                <pre className="text-white/80">{`# Network
rpc_url = "https://rpc.fantom.network"
chain_id = 250

# Contracts (Fantom Opera)
swapper_address = "0xcBc0C8919f95688b400723Bc742575723EF25074"
vault_address = "0x4E8d6150c1C1f453cc41431231189b8BDe88740F"

# Swap Settings
default_slippage = 0.5  # 0.5%
tx_deadline_minutes = 20`}</pre>
              </div>
            </div>
          </div>

          {/* Troubleshooting */}
          <div className="swap-card rounded-2xl p-8 mb-8">
            <h3 className="text-xl font-bold text-white mb-6 font-orbitron">Troubleshooting</h3>
            <div className="space-y-4">
              <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                <h4 className="text-white font-semibold mb-2">"chadswap: command not found"</h4>
                <p className="text-white/60 text-sm mb-2">Open a new terminal window, or run:</p>
                <CodeBlock command="source ~/.bashrc  # or ~/.zshrc for zsh" id="source" />
              </div>
              <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                <h4 className="text-white font-semibold mb-2">Ledger not detected</h4>
                <p className="text-white/60 text-sm">
                  Make sure your Ledger is unlocked and the Ethereum app is open.
                </p>
              </div>
              <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                <h4 className="text-white font-semibold mb-2">RPC errors</h4>
                <p className="text-white/60 text-sm mb-2">Try a different RPC endpoint in your config:</p>
                <CodeBlock command='rpc_url = "https://1rpc.io/ftm"' id="rpc" />
              </div>
            </div>
          </div>

          {/* Community Message */}
          <div className="text-center py-8">
            <p className="text-lg text-white/80 max-w-3xl mx-auto font-rajdhani">
              Giving access to liquidity to a locked LIF3 community 💙
            </p>
            <div className="mt-6 flex justify-center space-x-4">
              <a
                href="/"
                className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold rounded-xl transition-all duration-200 transform hover:scale-105"
              >
                Use Web App
              </a>
              <a
                href="https://github.com/GFTM/swap"
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-3 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold rounded-xl transition-all duration-200"
              >
                View Source
              </a>
            </div>
          </div>

        </div>
      </main>
    </div>
  );
}

export default CLIPage;
